import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { fmtRelative } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Plus, Copy, ShieldCheck, Users2, Eye, EyeOff, Loader2 } from "lucide-react";

export default function AdminWorkspaces() {
  const [ws, setWs] = useState([]);
  const [showProvision, setShowProvision] = useState(false);
  const [manageWs, setManageWs] = useState(null);
  const load = () => api.get("/admin/workspaces").then((r) => setWs(r.data));
  useEffect(() => { load(); }, []);
  return (
    <>
      <PageHeader
        eyebrow="Tenancy"
        title="Workspaces"
        description="All tenant workspaces on this platform."
        testid="admin-ws-header"
        actions={
          <Button className="rounded-none" onClick={() => setShowProvision(true)} data-testid="admin-provision-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Provision workspace
          </Button>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 py-8">
        <div className="border border-border">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border label-eyebrow text-[10px] bg-muted/40">
            <div className="col-span-3">Name</div>
            <div className="col-span-2">Slug</div>
            <div className="col-span-2">Plan</div>
            <div className="col-span-1">Currency</div>
            <div className="col-span-1 text-right">Members</div>
            <div className="col-span-1 text-right">Projects</div>
            <div className="col-span-1 text-right">Created</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {ws.map((w) => (
            <div key={w.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border items-center text-sm" data-testid={`admin-ws-row-${w.id}`}>
              <div className="col-span-3 font-medium truncate flex items-center gap-1.5">
                {w.name}
                {w.hide_owner_from_assignees && (
                  <span title="Owner hidden from assignee lists" className="text-[9px] uppercase tracking-widest bg-amber-100 text-amber-700 border border-amber-300 px-1 py-0.5 inline-flex items-center gap-0.5">
                    <ShieldCheck className="w-2.5 h-2.5" /> hidden
                  </span>
                )}
              </div>
              <div className="col-span-2 font-mono text-xs">{w.slug}</div>
              <div className="col-span-2"><span className="chip chip-pending">{w.plan_slug || "free"}</span></div>
              <div className="col-span-1 text-xs">{w.currency}</div>
              <div className="col-span-1 text-right">{w.member_count}</div>
              <div className="col-span-1 text-right">{w.project_count}</div>
              <div className="col-span-1 text-right text-xs text-muted-foreground">{fmtRelative(w.created_at)}</div>
              <div className="col-span-1 text-right">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-none h-7 text-xs"
                  onClick={() => setManageWs(w)}
                  data-testid={`admin-ws-manage-${w.id}`}
                >
                  <Users2 className="w-3 h-3 mr-1" /> Members
                </Button>
              </div>
            </div>
          ))}
          {ws.length === 0 && <div className="px-4 py-8 text-muted-foreground text-sm">No workspaces yet.</div>}
        </div>
      </div>
      {showProvision && (
        <ProvisionDialog onClose={() => setShowProvision(false)} onDone={() => { setShowProvision(false); load(); }} />
      )}
      {manageWs && (
        <ManageMembersDialog ws={manageWs} onClose={() => setManageWs(null)} onSaved={load} />
      )}
    </>
  );
}

function ProvisionDialog({ onClose, onDone }) {
  const [name, setName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [makeTargetOwner, setMakeTargetOwner] = useState(true);
  const [addSaHidden, setAddSaHidden] = useState(true);
  const [currency, setCurrency] = useState("USD");
  const [country, setCountry] = useState("IN");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!name.trim() || !ownerEmail.trim()) { toast.error("Name and owner email required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/provision-workspace", {
        name: name.trim(),
        owner_email: ownerEmail.trim(),
        make_target_owner: makeTargetOwner,
        add_sa_hidden: addSaHidden,
        currency,
        country_code: country || null,
      });
      setResult(data);
      toast.success("Workspace provisioned");
    } catch (e) { toast.error(apiErrorMessage(e, "Provisioning failed")); }
    finally { setBusy(false); }
  };

  const copyPw = async () => {
    if (!result?.temp_password) return;
    try { await navigator.clipboard.writeText(result.temp_password); toast.success("Temp password copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && (result ? onDone() : onClose())}>
      <DialogContent className="max-w-lg" data-testid="admin-provision-dialog">
        <DialogHeader>
          <DialogTitle>Provision workspace</DialogTitle>
        </DialogHeader>
        {result ? (
          <div className="space-y-3 text-sm">
            <div className="border p-3 bg-emerald-50 border-emerald-300">
              <div className="text-[10px] uppercase tracking-widest text-emerald-800">Success</div>
              <div className="font-semibold mt-1">{result.workspace.name}</div>
              <div className="text-xs text-muted-foreground">Slug · <code>{result.workspace.slug}</code></div>
            </div>
            <div className="border p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Owner</div>
              <div>{result.target_user.name} · <span className="font-mono text-xs">{result.target_user.email}</span></div>
              {result.temp_password && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-300">
                  <div className="text-[10px] uppercase tracking-widest text-amber-800 mb-1">Temporary password (share securely, shown ONCE)</div>
                  <div className="flex items-center gap-2">
                    <code className="text-sm font-mono bg-background px-2 py-1 border" data-testid="admin-provision-temp-pw">{result.temp_password}</code>
                    <Button size="sm" variant="outline" className="rounded-none" onClick={copyPw}><Copy className="w-3 h-3" /></Button>
                  </div>
                </div>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {result.memberships_created} membership row{result.memberships_created === 1 ? "" : "s"} created · {result.workspace.hide_owner_from_assignees ? "Owner hidden from assignee lists" : "Owner visible in assignee lists"}.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Workspace name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Legacy Life Spaces" className="mt-1" data-testid="admin-provision-name" />
            </div>
            <div>
              <Label className="text-xs">Owner email *</Label>
              <Input value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} placeholder="client@example.com" className="mt-1 font-mono" data-testid="admin-provision-email" />
              <div className="text-[10px] text-muted-foreground mt-0.5">If the email isn&apos;t registered, a stub user is created and a temporary password is returned to you.</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Currency</Label>
                <Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} className="mt-1 font-mono" />
              </div>
              <div>
                <Label className="text-xs">Country code</Label>
                <Input value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} maxLength={2} className="mt-1 font-mono" />
              </div>
            </div>
            <div className="border p-3 space-y-2 bg-muted/30">
              <label className="flex items-start gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={makeTargetOwner} onChange={(e) => setMakeTargetOwner(e.target.checked)} data-testid="admin-provision-make-owner" className="mt-0.5" />
                <span>
                  <b>Make the client the workspace owner</b> (recommended)
                  <div className="text-muted-foreground mt-0.5">Uncheck to keep yourself as owner_id (silent super-admin ownership). The client is added as workspace_admin.</div>
                </span>
              </label>
              <label className="flex items-start gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={addSaHidden} onChange={(e) => setAddSaHidden(e.target.checked)} data-testid="admin-provision-add-hidden" className="mt-0.5" />
                <span>
                  <b>Add me as a hidden workspace_admin</b>
                  <div className="text-muted-foreground mt-0.5">Keeps you invisible in every assignee dropdown but you retain login + management access.</div>
                </span>
              </label>
            </div>
          </div>
        )}
        <DialogFooter>
          {result ? (
            <Button className="rounded-none" onClick={onDone} data-testid="admin-provision-done">Done</Button>
          ) : (
            <>
              <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
              <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="admin-provision-submit">
                {busy ? "Provisioning…" : "Provision workspace"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// R157b · Feb 2026 — Super Admin bulk member management for a workspace.
// Lets SAs retroactively flip `hidden_from_lists` on any existing
// membership (useful for older SA-owned client workspaces created
// before R157) + toggle the workspace-level `hide_owner_from_assignees`
// flag.
function ManageMembersDialog({ ws, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [flagSaving, setFlagSaving] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/workspaces/${ws.id}/members-all`);
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); onClose(); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ws.id]);

  const toggleHidden = async (m) => {
    setSavingId(m.id);
    try {
      const next = !m.hidden_from_lists;
      await api.patch(`/admin/workspaces/${ws.id}/members/${m.id}/hidden`, { hidden_from_lists: next });
      toast.success(next ? `Hidden ${m.email || m.name || "member"} from dropdowns` : `Revealed ${m.email || m.name || "member"}`);
      setData((d) => d ? {
        ...d,
        members: d.members.map((x) => x.id === m.id ? { ...x, hidden_from_lists: next } : x),
      } : d);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSavingId(null); }
  };

  const toggleWsFlag = async (v) => {
    setFlagSaving(true);
    try {
      await api.patch(`/admin/workspaces/${ws.id}/hide-owner-flag`, { hide_owner_from_assignees: v });
      toast.success(v ? "Owner is now hidden from assignee lists" : "Owner is visible everywhere");
      setData((d) => d ? { ...d, workspace: { ...d.workspace, hide_owner_from_assignees: v } } : d);
      onSaved && onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setFlagSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-manage-members-dialog">
        <DialogHeader>
          <DialogTitle>Manage members — {ws.name}</DialogTitle>
        </DialogHeader>
        {busy && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
        {data && (
          <div className="space-y-4">
            {/* Workspace-level toggle */}
            <div className="border p-3 bg-muted/30">
              <label className="flex items-start gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!data.workspace.hide_owner_from_assignees}
                  onChange={(e) => toggleWsFlag(e.target.checked)}
                  disabled={flagSaving}
                  data-testid="admin-manage-hide-owner-flag"
                  className="mt-0.5"
                />
                <span>
                  <b>Hide workspace owner from assignee lists</b>
                  <div className="text-muted-foreground mt-0.5">Applies to every user except the owner themselves. Automatic when the owner is a Super Admin.</div>
                </span>
              </label>
            </div>

            {/* Members table */}
            <div className="border">
              <div className="px-3 py-2 bg-muted/40 border-b text-[10px] uppercase tracking-widest text-muted-foreground">
                {data.members.length} member{data.members.length === 1 ? "" : "s"}
              </div>
              <ul className="divide-y">
                {data.members.map((m) => (
                  <li key={m.id} className="px-3 py-2 flex items-center gap-3" data-testid={`admin-member-row-${m.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm truncate">{m.name || m.email}</span>
                        {m.is_owner && (
                          <span className="text-[9px] uppercase tracking-widest bg-primary/10 text-primary border border-primary/30 px-1 py-0.5">owner</span>
                        )}
                        {m.is_super_admin && (
                          <span className="text-[9px] uppercase tracking-widest bg-purple-100 text-purple-700 border border-purple-300 px-1 py-0.5">SA</span>
                        )}
                        {m.hidden_from_lists && (
                          <span className="text-[9px] uppercase tracking-widest bg-amber-100 text-amber-700 border border-amber-300 px-1 py-0.5 inline-flex items-center gap-0.5">
                            <EyeOff className="w-2.5 h-2.5" /> hidden
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground font-mono">{m.email}</div>
                      <div className="text-[10px] text-muted-foreground">Role · {m.role} · Status · {m.status}</div>
                    </div>
                    <Button
                      size="sm"
                      variant={m.hidden_from_lists ? "outline" : "ghost"}
                      className="rounded-none h-7 text-xs"
                      onClick={() => toggleHidden(m)}
                      disabled={savingId === m.id}
                      data-testid={`admin-member-toggle-hidden-${m.id}`}
                      title={m.hidden_from_lists ? "Currently hidden — click to reveal" : "Click to hide from assignee dropdowns"}
                    >
                      {savingId === m.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : m.hidden_from_lists ? (
                        <><Eye className="w-3 h-3 mr-1" /> Reveal</>
                      ) : (
                        <><EyeOff className="w-3 h-3 mr-1" /> Hide</>
                      )}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="text-[10px] text-muted-foreground">
              Hidden members do NOT appear in assignee dropdowns, task-owner pickers, meeting-invitee lists, etc. They still see themselves and retain login + management access.
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

